/**
 * API Action Creators and related tokens and types.
 *
 * @module actions/Api
 */
import { createAction, createCustomAction } from 'typesafe-actions';

import { type ValueOf } from 'src/types';

import { ActionTypes, ApiEndpoints, ApiToken, ServiceHostTypes } from '../constants/ApiActions';
import { getActionPrefix } from '../lib/ApiUtils';

// type MiddlewareApiAction<
//   ActionParams extends Record<string, $TSFixMe> | undefined = undefined,
//   Action extends { type: string; actionParams?: ActionParams; [ApiToken]: IBaseRequestParams } = {
//     type: string;
//     [ApiToken]: IBaseRequestParams;
//   },
// > = Pick<Action, 'type' | 'actionParams'> & Action[typeof ApiToken];

/**
 * Miscellaneous API actions
 */

interface SendServiceErrorToMiddlewarePayload {
  action: {
    [ApiToken]: $TSFixMe;
  };
  error: $TSFixMe;
}

export const sendServiceErrorToMiddleware = createAction(
  ActionTypes.SEND_SERVICE_ERROR_TO_MIDDLEWARE,
  (params: $TSFixMe, error: $TSFixMe) => ({
    action: {
      [ApiToken]: params,
    },
    error,
  }),
)<SendServiceErrorToMiddlewarePayload>();
export type SendServiceErrorToMiddlewareAction = ReturnType<typeof sendServiceErrorToMiddleware>;

/**
 * Util Action Creator Function to streamline API Endpoint generic typing logic
 *
 * @param endpoint
 * @param actionCreator
 */
const createApiAction = <Endpoint extends ValueOf<typeof ApiEndpoints>, Params, Payload>(
  endpoint: Endpoint,
  actionCreator: (params: Params) => Payload,
) =>
  createCustomAction<
    ReturnType<typeof getActionPrefix<Endpoint>>,
    Params extends never ? never : [Params],
    Payload
  >(getActionPrefix(endpoint), actionCreator);

/**
 * Listener (User) API Action Creators
 */

interface ApiEmailPasswordParams {
  username: string;
  redirectPath: string;
}

/**
 * Action creator for sending a password reset email to the listener,
 * which includes a link that takes them to the reset password flow.
 *
 * @param {string} username - username to identify user
 * @param {string} redirectPath - If a redirectPath is specified,
 * the browser will be directed there after password reset
 */
export const ApiEmailPassword = createApiAction(
  ApiEndpoints.EMAIL_PASSWORD,
  ({ username, redirectPath }: ApiEmailPasswordParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.EMAIL_PASSWORD,
      content: {
        username,
        redirectPath,
      },
    },
    username,
  }),
);
export type ApiEmailPasswordAction = ReturnType<typeof ApiEmailPassword>;

/**
 * Action creator for validating password reset token
 *
 * @param {string} resetToken - a password reset token that needs to be validated
 */
export const ApiValidatePasswordResetToken = createApiAction(
  ApiEndpoints.VALIDATE_PASSWORD_RESET_TOKEN,
  (resetToken: string) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.VALIDATE_PASSWORD_RESET_TOKEN,
      content: {
        resetToken,
      },
    },
  }),
);
export type ApiValidatePasswordResetTokenAction = ReturnType<typeof ApiValidatePasswordResetToken>;

interface ApiUpdateAccountParams {
  currentUsername: string;
  currentPassword: string;
  newUsername: string;
  newPassword: string;
  newZipCode: string;
  newGender: string;
  newBirthYear: string;
  newBirthMonth: string;
  newBirthDay: string;
  enableExplicitContentFilter: boolean;
  explicitPIN: string;
}

/**
 * Action creator for updating registration information and/or the
 * explicit content filter. Options that are undefined will not be sent
 * in the API request.
 *
 * @param {string} currentUsername - current username
 * @param {string} currentPassword - current password
 * @param {string} newUsername - username that should be set
 * @param {string} newPassword - password that should be set
 * @param {string} newZipCode - zip code that should be set
 * @param {string} newGender - gender that should be set
 * @param {string} newBirthYear - birth year that should be set
 * @param {string} newBirthMonth - birth month that should be set
 * @param {string} newBirthDay - birth day that should be set
 * @param {boolean} enableExplicitContentFilter - whether to enable
 * the explicit content filter for this user
 * @param {string} explicitPIN - PIN for setting explicit filter, must
 * be included if setting enableExplicitContentFilter
 */
export const ApiUpdateAccount = createApiAction(
  ApiEndpoints.UPDATE_ACCOUNT,
  ({
    currentUsername,
    currentPassword,
    newUsername,
    newPassword,
    newZipCode,
    newGender,
    newBirthYear,
    newBirthMonth,
    newBirthDay,
    enableExplicitContentFilter,
    explicitPIN,
  }: ApiUpdateAccountParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.UPDATE_ACCOUNT,
      content: {
        currentUsername,
        currentPassword,
        newUsername,
        newPassword,
        newZipCode,
        newGender,
        newBirthYear,
        newBirthMonth,
        newBirthDay,
        enableExplicitContentFilter,
        explicitPIN,
      },
    },
  }),
);
export type ApiUpdateAccountAction = ReturnType<typeof ApiUpdateAccount>;

interface ApiUpdateSettingsParams {
  allowProfileComments: boolean;
  artistAudioMessagesEnabled: boolean;
  artistMessageSettings: $TSFixMe[];
  artistPromoEmailsEnabled: boolean;
  autoplayEnabled: boolean;
  birthYear: string;
  emailOptOut: boolean;
  facebookAutoShareEnabled: boolean;
  facebookAutoShareFollows: boolean;
  facebookAutoShareLikes: boolean;
  facebookAutoShareTrackPlay: boolean;
  gender: string;
  highQualityStreamingEnabled: boolean;
  notifyOnComment: boolean;
  notifyOnFollow: boolean;
  password: string;
  postalCode: string;
  profilePrivate: boolean;
  seenWebPremiumWelcome: boolean;
  username: string;
  webClient2016OptedIn: boolean;
}

/**
 * Action creator forUpdates one or more of the listener's privacy, notification,
 * Facebook auto-share and/or artist audio message settings.
 * Any properties not included in the request will remain unchanged.
 *
 * @param {boolean} allowProfileComments - whether to allow comments on profile
 * @param {boolean} artistAudioMessagesEnabled - whether user can get
 * artist audio messages
 * @param {Array} artistMessageSettings - settings to be used for artist
 * messages. Contains objects that contain setting parameters (stationIds, and whether
 * to enable them).
 * @param {boolean} artistPromoEmailsEnabled - whether user can get
 * artist promo emails.
 * @param {boolean} facebookAutoShareEnabled - whether FB auto share is enabled
 * @param {string} facebookAutoShareFollows - whether to auto share follows
 * to facebook.
 * @param {boolean} facebookAutoShareLikes - whether to auto share likes
 * to facebook.
 * @param {boolean} facebookAutoShareTrackPlay - whether to auto share
 * track plays to facebook
 * @param {boolean} profilePrivate - whether user profile is private
 * @param {boolean} emailOptOut - whether to opt out of emails
 * @param {boolean} notifyOnComment - whether to notify on comment
 * @param {boolean} notifyOnFollow - whether to notify on follow
 */
export const ApiUpdateSettings = createApiAction(
  ApiEndpoints.UPDATE_SETTINGS,
  ({
    allowProfileComments,
    artistAudioMessagesEnabled,
    artistMessageSettings,
    artistPromoEmailsEnabled,
    autoplayEnabled,
    birthYear,
    emailOptOut,
    facebookAutoShareEnabled,
    facebookAutoShareFollows,
    facebookAutoShareLikes,
    facebookAutoShareTrackPlay,
    gender,
    highQualityStreamingEnabled,
    notifyOnComment,
    notifyOnFollow,
    password,
    postalCode,
    profilePrivate,
    seenWebPremiumWelcome,
    username,
    webClient2016OptedIn,
  }: ApiUpdateSettingsParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.UPDATE_SETTINGS,
      content: {
        allowProfileComments,
        artistAudioMessagesEnabled,
        artistMessageSettings,
        artistPromoEmailsEnabled,
        autoplayEnabled,
        birthYear,
        emailOptOut,
        facebookAutoShareEnabled,
        facebookAutoShareFollows,
        facebookAutoShareLikes,
        facebookAutoShareTrackPlay,
        gender,
        highQualityStreamingEnabled,
        notifyOnComment,
        notifyOnFollow,
        password,
        postalCode,
        profilePrivate,
        seenWebPremiumWelcome,
        username,
        webClient2016OptedIn,
      },
    },
  }),
);
export type ApiUpdateSettingsAction = ReturnType<typeof ApiUpdateSettings>;

/**
 * Action creator for getting the profile information for a listener.
 * Information will be returned for any listener, but only a subset of properties
 * is returned if the profile is set to "private". If a profile is private,
 * then only the owner of that profile will be returned full profile info.
 *
 * @param {string} webname - name of the profile to look up
 */
export const ApiGetProfile = createApiAction(ApiEndpoints.GET_PROFILE, (webname: string) => ({
  [ApiToken]: {
    endpoint: ApiEndpoints.GET_PROFILE,
    content: {
      webname,
    },
  },
}));
export type ApiGetProfileAction = ReturnType<typeof ApiGetProfile>;

interface ApiSetProfileParams {
  fullName: string;
  homeTown: string;
  school: string;
  employer: string;
  bio: string;
}

/**
 * Action creator for setting information on the profile page.
 *
 * @param {string} fullName - full name for profile
 * @param {string} homeTown - hometown for the profile
 * @param {string} school - school for the profile
 * @param {string} employer - employer for the profile
 * @param {string} bio - bio for the profile
 */
export const ApiSetProfile = createApiAction(
  ApiEndpoints.SET_PROFILE,
  ({ fullName, homeTown, school, employer, bio }: ApiSetProfileParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.SET_PROFILE,
      content: {
        fullName,
        homeTown,
        school,
        employer,
        bio,
      },
    },
  }),
);
export type ApiSetProfileAction = ReturnType<typeof ApiSetProfile>;

/**
 * Login API Action Creators
 */

/**
 * Action creator for indicating that the listener has seen a bar message
 * and 'dismissed' (closed) it.
 *
 * @param {string} messageKey - the key for the message
 */
export const ApiAccountMessageDismissed = createApiAction(
  ApiEndpoints.ACCOUNT_MESSAGE_DISMISSED,
  (messageKey: string) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.ACCOUNT_MESSAGE_DISMISSED,
      content: {
        messageKey,
      },
    },
  }),
);
export type ApiAccountMessageDismissedAction = ReturnType<typeof ApiAccountMessageDismissed>;

/**
 * Music API Action Creators
 */

/**
 * Action creator that gets a list Genres Categories
 *
 * @returns {[{}]} - List of Categories entities
 */
export const ApiGetGenreCategories = createApiAction(ApiEndpoints.FETCH_GENRE_CATEGORIES, () => ({
  [ApiToken]: {
    endpoint: ApiEndpoints.FETCH_GENRE_CATEGORIES,
    method: 'get' as const,
  },
}));
export type ApiGetGenreCategoriesAction = ReturnType<typeof ApiGetGenreCategories>;

/**
 * Action creator that gets a list Genre Stations for a given Genre Category
 *
 * @returns {[{}]} - List of Genre Stations
 */
export const ApiGetGenresByCategoryId = createApiAction(
  ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID,
  (categoryToken: $TSFixMe) => ({
    categoryToken,
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID,
      method: 'get' as const,
      params: {
        categoryToken,
      },
    },
  }),
);
export type ApiGetGenresByCategoryIdAction = ReturnType<typeof ApiGetGenresByCategoryId>;

/**
 * Action creator that gets a list Genre Stations for a given Genre Category
 *
 * @returns {[{}]} - List of Genre Stations
 */
export const ApiGetGenresByToken = createApiAction(
  ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID,
  (genreTokens: $TSFixMe) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_GENRES_BY_CATEGORY_ID,
      method: 'get' as const,
      params: {
        genreTokens,
      },
    },
  }),
);
export type ApiGetGenresByTokenAction = ReturnType<typeof ApiGetGenresByToken>;

/**
 * Playlist API Action Creators
 */

// TODO: We still need an action creator for playlist/getContent. This is tied to Chronos V2 and still a work in progress.
// When it is available we should create an action creator for it here

/**
/**
 * Stations API Action Creators
 */

/**
 * ApiGetOrSetShuffleStations - Action creator for fetching or setting the shuffle station.
 *
 * @param  {Array[String]} stationIds - list of stationIds. If the argument is not passed
 * we will just get the shuffle station back with all the valid shuffle stations. If it is passed
 * then the shuffle station will set those as the stationsToShuffle
 */
export const ApiGetOrSetShuffleStations = createApiAction(
  ApiEndpoints.GET_OR_SET_SHUFFLE_STATIONS,
  (stationIds: string[]) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_OR_SET_SHUFFLE_STATIONS,
      content: {
        stationIds,
      },
    },
  }),
);
export type ApiGetOrSetShuffleStationsAction = ReturnType<typeof ApiGetOrSetShuffleStations>;

interface ApiAddSeedParams {
  stationId: string;
  musicId: string;
}

/**
 * Action creator that adds a seed to a station
 *
 * @param  {string} stationId - ID for station
 * @param  {string} musicId - musicID of seed to add
 */
export const ApiAddSeed = createApiAction(
  ApiEndpoints.ADD_SEED,
  ({ stationId, musicId }: ApiAddSeedParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.ADD_SEED,
      content: {
        stationId,
        musicId,
      },
    },
  }),
);
export type ApiAddSeedAction = ReturnType<typeof ApiAddSeed>;

interface ApiDeleteSeedParams {
  stationId: string;
  musicId: string;
}

/**
 * Action creator that deletes a seed from a station
 *
 * @param  {string} stationId - ID for station
 * @param  {string} musicId - musicID of seed to delete
 */
export const ApiDeleteSeed = createApiAction(
  ApiEndpoints.DELETE_SEED,
  ({ stationId, musicId }: ApiDeleteSeedParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.DELETE_SEED,
      content: {
        stationId,
        musicId,
      },
    },
  }),
);
export type ApiDeleteSeedAction = ReturnType<typeof ApiDeleteSeed>;

interface ApiGetNarrativeParams {
  trackToken: string;
  trackId: string;
  stationId: string;
  isAPSEnabled: boolean;
}

/**
 * Action creator for getting narrative
 *
 * @param  {string} trackToken - token for the track
 */
export const ApiGetNarrative = createApiAction(
  ApiEndpoints.FETCH_NARRATIVE,
  ({ trackToken, trackId, stationId, isAPSEnabled }: ApiGetNarrativeParams) => ({
    updateEntitiesWith: { trackToken },
    [ApiToken]: {
      endpoint: ApiEndpoints.FETCH_NARRATIVE,
      content: {
        musicId: trackId,
        stationId,
      },
    },
    isAPSEnabled,
  }),
);
export type ApiGetNarrativeAction = ReturnType<typeof ApiGetNarrative>;

interface ApiGetFeedbackParams {
  pageSize: string;
  startIndex: boolean;
  mostRecent: boolean;
  webname: string;
}

/**
 * Action creator for getting positive feedback sorted from most recent to least
 *
 * @param  {string} pageSize - number of results to return in call. default 5 if unspecified, max 100
 * @param  {boolean} startIndex - starting index incrementing by 1 for each thumb
 * @param  {boolean} mostRecent - sorts feedback by recency
 * @param  {string} webname - user retrieving feedback
 */
export const ApiGetFeedback = createApiAction(
  ApiEndpoints.GET_FEEDBACK,
  ({ pageSize, startIndex, mostRecent, webname }: ApiGetFeedbackParams) => ({
    mostRecent,
    webname,
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_FEEDBACK,
      content: {
        pageSize,
        startIndex,
        webname,
        mostRecent,
      },
    },
  }),
);
export type ApiGetFeedbackAction = ReturnType<typeof ApiGetFeedback>;

interface ApiGetStationFeedbackParams {
  pageSize: string;
  startIndex: boolean;
  stationId: string;
  positive: boolean;
  mostRecent: boolean;
}

/**
 * Action creator for getting all feedback for a station
 *
 * @param  {string} pageSize - number of results to return in call. default 5 if unspecified, max 100
 * @param  {boolean} startIndex - starting index incrementing by 1 for each thumb
 * @param  {string} stationId - the station we want to get feedback for
 * @param  {boolean} positive - whether to retrieve positive or negative feedback
 * @param  {boolean} mostRecent - if a track needs to be prepended to the list
 */
export const ApiGetStationFeedback = createApiAction(
  ApiEndpoints.GET_STATION_FEEDBACK,
  ({ pageSize, startIndex, stationId, positive, mostRecent }: ApiGetStationFeedbackParams) => ({
    stationId,
    mostRecent,
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_STATION_FEEDBACK,
      content: {
        pageSize,
        startIndex,
        stationId,
        positive,
      },
    },
  }),
);
export type ApiGetStationFeedbackAction = ReturnType<typeof ApiGetStationFeedback>;

interface ApiFlagMessageParams {
  artistMessageId: string;
  artistUid: string;
  reason: $TSFixMe;
  trackId: string;
}

/**
 * Action creator that flags current artist message as inappropiate
 *
 * @param  {string} artistMessageId - ID for artist message
 * @param  {string} artistUid - Uid for artist
 */
export const ApiFlagMessage = createApiAction(
  ApiEndpoints.FLAG_MESSAGE,
  ({ artistMessageId, artistUid, reason, trackId }: ApiFlagMessageParams) => ({
    updateEntitiesWith: {
      trackId,
    },
    [ApiToken]: {
      endpoint: ApiEndpoints.FLAG_MESSAGE,
      content: {
        artistMessageId,
        artistUid,
        reason,
      },
    },
  }),
);
export type ApiFlagMessageAction = ReturnType<typeof ApiFlagMessage>;

/**
 * Sends information to the server when the music has been paused or stopped due to user action.
 *
 * @param  {boolean} async - whether this request should be done asynchronously or not
 * in the case of window.onunload event we want to fire this action synchronously, since all
 * async XHR requests get flushed out on window.onunload.
 */
export const ApiPlaybackPaused = createApiAction(ApiEndpoints.PLAYBACK_PAUSED, (async = true) => ({
  [ApiToken]: {
    endpoint: ApiEndpoints.PLAYBACK_PAUSED,
    sync: !async,
  },
}));
export type ApiPlaybackPausedAction = ReturnType<typeof ApiPlaybackPaused>;

interface ApiFacebookConnectParams {
  facebookId: string;
  facebookName: string;
  accessToken: string;
  accessTokenExpires: string;
}

/**
 * @param  {string} facebookId - facebookID for user
 * @param  {string} facebookName - facebookName of user
 * @param  {string} accessToken - accessToken for connecting to facebook
 * @param  {string} accessTokenExpires - the time when the accessToken expires
 */
export const ApiFacebookConnect = createApiAction(
  ApiEndpoints.FACEBOOK_CONNECT,
  ({ facebookId, facebookName, accessToken, accessTokenExpires }: ApiFacebookConnectParams) => ({
    payload: {
      facebookId,
      facebookName,
      accessToken,
      accessTokenExpires,
    },
    [ApiToken]: {
      endpoint: ApiEndpoints.FACEBOOK_CONNECT,
      content: {
        facebookId,
        facebookName,
        accessToken,
        accessTokenExpires,
      },
    },
  }),
);
export type ApiFacebookConnectAction = ReturnType<typeof ApiFacebookConnect>;

export const ApiFacebookDisconnect = createApiAction(ApiEndpoints.FACEBOOK_DISCONNECT, () => ({
  [ApiToken]: {
    endpoint: ApiEndpoints.FACEBOOK_DISCONNECT,
  },
}));
export type ApiFacebookDisconnectAction = ReturnType<typeof ApiFacebookDisconnect>;

interface ApiSetUserTokensParams {
  token: string;
  secret: string;
  twitterHandle: string;
}

/**
 * @param {string} token - token for twitter oauth
 * @param {string} secret - secret for twitter oauth
 * @param {string} twitterHandle - screenName of user on twitter
 */
export const ApiSetUserTokens = createApiAction(
  ApiEndpoints.SET_TWITTER_TOKENS,
  ({ token, secret, twitterHandle }: ApiSetUserTokensParams) => ({
    payload: {
      token,
      secret,
      twitterHandle,
    },
    [ApiToken]: {
      endpoint: ApiEndpoints.SET_TWITTER_TOKENS,
      content: {
        token,
        secret,
        twitterHandle,
      },
    },
  }),
);
export type ApiSetUserTokensAction = ReturnType<typeof ApiSetUserTokens>;

export const ApiGetRequestToken = createApiAction(
  ApiEndpoints.GET_REQUEST_TOKEN,
  (query: $TSFixMe) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_REQUEST_TOKEN,
      serviceHostType: ServiceHostTypes.TWITTER,
      query,
    },
  }),
);
export type ApiGetRequestTokenAction = ReturnType<typeof ApiGetRequestToken>;

interface ApiGetAuthTokenParams {
  queryParams: $TSFixMe;
  formData: $TSFixMe;
}

export const ApiGetAuthToken = createApiAction(
  ApiEndpoints.GET_AUTH_TOKEN,
  ({ queryParams, formData }: ApiGetAuthTokenParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.GET_AUTH_TOKEN,

      content: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      params: queryParams,
      serviceHostType: ServiceHostTypes.TWITTER,
    },
  }),
);
export type ApiGetAuthTokenAction = ReturnType<typeof ApiGetAuthToken>;

interface ApiRegisterImpressionParams {
  trackerUrl: $TSFixMe;
  urlType: $TSFixMe;
  adRequestMetaData: $TSFixMe;
  adToken: $TSFixMe;
  stationId: string;
  httpReferrer: $TSFixMe;
}

export const ApiRegisterImpression = createApiAction(
  ApiEndpoints.REGISTER_IMPRESSION,
  (apiParams: ApiRegisterImpressionParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.REGISTER_IMPRESSION,
      method: 'post' as const,
      content: apiParams,
    },
  }),
);
export type ApiRegisterImpressionAction = ReturnType<typeof ApiRegisterImpression>;

interface ApiRegisterVideoAdImpressionParams {
  creativeId: string;
  secondsPlayed: number;
  adLength: number;
  httpReferrer: $TSFixMe;
}

/**
 * Is currently only used to register the _start_ of video ad playback. Cannot
 * be issued until after the _duration_ value of the video ad can be determined.
 *
 * @param {string} creativeId The id passed in with the XML payload of the ad
 * @param {number} secondsPlayed Should always be -1
 * @param {number} adLength The _duration_ of the ad video content
 */
export const ApiRegisterVideoAdImpression = createApiAction(
  ApiEndpoints.REGISTER_VIDEO_AD_IMPRESSION,
  ({
    creativeId = '',
    secondsPlayed = -1,
    adLength = 0,
    httpReferrer,
  }: ApiRegisterVideoAdImpressionParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.REGISTER_VIDEO_AD_IMPRESSION,
      method: 'post' as const,
      content: {
        creativeId,
        secondsPlayed,
        // for some reason only accepts integers
        adLength: Math.round(adLength),
        httpReferrer,
      },
    },
  }),
);
export type ApiRegisterVideoAdImpressionAction = ReturnType<typeof ApiRegisterVideoAdImpression>;

interface ApiDeleteListenerParams {
  email: string;
  password: string;
  listenerId: string;
}

export const ApiDeleteListener = createApiAction(
  ApiEndpoints.DELETE_LISTENER,
  ({ email, password, listenerId }: ApiDeleteListenerParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.DELETE_LISTENER,
      content: {
        email,
        password,
        listenerId,
      },
    },
  }),
);
export type ApiDeleteListenerAction = ReturnType<typeof ApiDeleteListener>;

interface ApiLogOutAllDevicesParams {
  password: string;
}

export const ApiLogOutAllDevices = createApiAction(
  ApiEndpoints.LOG_OUT_ALL_DEVICES,
  ({ password }: ApiLogOutAllDevicesParams) => ({
    [ApiToken]: {
      endpoint: ApiEndpoints.LOG_OUT_ALL_DEVICES,
      content: {
        password,
      },
    },
  }),
);
export type ApiLogOutAllDevicesAction = ReturnType<typeof ApiLogOutAllDevices>;

interface ApiBrokenAdParams extends $TSFixMe {}

export const ApiBrokenAd = createApiAction(ApiEndpoints.BROKEN_AD, (params: ApiBrokenAdParams) => ({
  [ApiToken]: {
    endpoint: ApiEndpoints.BROKEN_AD,
    method: 'post' as const,
    content: params,
  },
}));
export type ApiBrokenAdAction = ReturnType<typeof ApiBrokenAd>;
